
import { NodeCanvasState, useNodeCanvasStore } from '../store';
import { drawLines } from '../components/canvas/drawCanvas';
import EditNodeBox from '../components/EditNodeBox.vue';
import AddTopicConnectionPrompt from '../components/AddTopicConnectionPrompt.vue';
import ServiceConnectionPromt from '../components/ServiceConnectionPromt.vue';
import EditTopicBox from '../components/EditTopicBox.vue';
import NodeParams from '../components/NodeParams.vue';
import EditServiceBox from '../components/EditServiceBox.vue';
import AddNewMsgType from '../components/AddNewMsgType.vue';
import PullCodes from '../components/PullCodes.vue';
import { Service } from '../components/storeClasses/service';
import { Node } from '../components/storeClasses/node';
import { Topic } from '../components/storeClasses/topic';
import { defineComponent } from 'vue';
import { allWorkspacesStateSingleton } from '@/firebase/database';
import MountedExecute from '@/modules/packageCanvas/modules/MountedExecute.vue';
import ActiveMessages from '@/modules/nodesCanvasModule/components/ActiveMessages.vue';
import { Store } from 'pinia';
import { GlobalStoreState, useGlobalStore } from '@/modules/globalModule/store';
import { addNewNode, addNodes } from '../components/canvas/drawNode';
import { addNewTopic, initTopics } from '../components/canvas/drawTopic';
import { addNewService, addServices } from '../components/canvas/drawService';
import { drawLinesToOuterPackages } from '../components/canvas/drawOuterPackage';
const cjs = createjs;
export default defineComponent({
  name: 'NodesCanvasPage',
  components: {
    EditNodeBox,
    EditTopicBox,
    AddTopicConnectionPrompt,
    NodeParams,
    ServiceConnectionPromt,
    EditServiceBox,
    AddNewMsgType,
    PullCodes,
    MountedExecute,
    ActiveMessages
  },
  data(): NodeCanvasData {
    return {
      ncStore: useNodeCanvasStore(),
      editing: false,
      mouseInCanvas: false,
      editingNode: false,
      editingTopic: false,
      isShowParams: false,
      stage: undefined as any,
      db: allWorkspacesStateSingleton(),
      connectingNode: null,
      globalStore: useGlobalStore(),
      saveText: ''
    };
  },
  created() {
    this.stage = new cjs.Stage('node-canvas');
    createjs.Touch.enable(this.stage);
  },
  methods: {
    // Have to call this after the component is loaded, or else the database will be out of sync
    async reMount() {
      const globalStore = useGlobalStore();
      // Recreate stage as HTML now exists
      this.stage = new cjs.Stage('node-canvas');
      createjs.Touch.enable(this.stage);
      if (!this.globalStore.loaded)
        await globalStore.loadWorkspace(
          this.$route.params.workspaceID as string
        );
      this.ncStore.initStore(
        this.$route.params.packageName as string,
        this.$route.params.packageID as string,
        this.$route.params.workspaceID as string
      );
      addNodes(this.stage);
      initTopics(this.stage);
      addServices(this.stage);
      drawLinesToOuterPackages(this.stage);
      createjs.Ticker.addEventListener('tick', this.onTick);
      this.stage.addEventListener('stagemouseup', this.mouseUp);
      this.stage.mouseMoveOutside = true;
      createjs.Ticker.framerate = 60;
      // size the canvas
      fitToContainer(this.$refs['node-canvas'] as HTMLCanvasElement);

      // add event for connecting nodes to external topics
      this.stage.addEventListener('nodeEdit', (obj: any) => {
        this.connectingNode = obj.node;
      });
      this.stage.addEventListener('nodeEditUp', () => {
        this.connectingNode = null;
      });

      this.setEditing(true);
      // autosave
      const autosave = () => {
        this.saveCanvas();
        setTimeout(() => {
          if (this.$route.name?.toString().includes('NodesCanvas')) autosave();
        }, 180000);
      };
      autosave();
      // save on navigation
      this.$router.beforeEach((to, from) => {
        if (from.name?.toString().includes('NodesCanvas')) this.saveCanvas();
      });
    },
    saveCanvas() {
      const globalStore = useGlobalStore();
      globalStore.saveData();
      this.saveText = 'Saved!';
      setTimeout(() => (this.saveText = ''), 5000);
    },
    onTick() {
      if (this.$route.name !== 'NodesCanvas.NodesPage') return;
      fitToContainer(this.$refs['node-canvas'] as HTMLCanvasElement);
      drawLines(this.stage);
      drawLinesToOuterPackages(this.stage);
      this.editBoxChecks();
      this.stage.update();
    },
    editClicked() {
      this.setEditing(!this.editing);
    },
    setEditing(editing: boolean = true) {
      this.editing = editing;
      let editEvent = new createjs.Event('editing', false, false);
      editEvent.set({ editing: this.editing });
      this.stage.dispatchEvent(editEvent);
    },
    mouseUp(evt: Event) {
      this.ncStore.currDragOffset = { x: 0, y: 0 };
    },
    touchStart(e: TouchEvent) {
      // Prevent default to stop screen drag on mobile devices
      e.preventDefault();
    },
    editDragEnd(e: MouseEvent | TouchEvent, type: string) {
      // Set page x y events dynamically based on touch or not
      const evt = { pageX: 0, pageY: 0 };
      if (e instanceof MouseEvent) {
        // Simply set event to pageX, pageY
        evt.pageX = e.pageX;
        evt.pageY = e.pageY;
      } else {
        // changedTouches because the finger is moved
        evt.pageX = e.changedTouches[0].pageX;
        evt.pageY = e.changedTouches[0].pageY;
      }
      if (!this.withinCanvas(evt.pageX, evt.pageY)) return;

      const bounds = document.querySelector('canvas')?.getBoundingClientRect();
      if (bounds === undefined) {
        return;
      }
      const x = evt.pageX - bounds.left;
      const y = evt.pageY - bounds.top;
      console.log(type);
      this.spawnCanvasObject(type, x, y);
    },
    withinCanvas(x: number, y: number) {
      let bounds = document.querySelector('canvas')?.getBoundingClientRect();
      if (bounds === undefined) {
        return;
      }
      return (
        x > bounds.left &&
        x < bounds.right &&
        y > bounds.top &&
        y < bounds.bottom
      );
    },
    // Checks if either edit node or edit topic boxes are open, performs appropriate
    editBoxChecks() {
      if (this.ncStore.editingNodeId !== 'undefined') {
        this.editingNode = true;
      } else if (this.editingNode) {
        // use this.editingNode so only runs once
        this.editingNode = false;
        this.stage.dispatchEvent('editNodeEnd');
      }
      if (this.ncStore.editingTopicId !== 'undefined' && !this.editingTopic) {
        this.editingTopic = true;
      } else if (
        this.ncStore.editingTopicId === 'undefined' &&
        this.editingTopic
      ) {
        // use this.editingTopic so only runs once
        this.editingTopic = false;
        this.stage.dispatchEvent('editTopicEnd');
      }
    },
    toggleIsShowParams(isShow: boolean) {
      this.isShowParams = isShow;
    },
    exitNodesCanvas() {
      this.$router.push('/packageCanvas/' + this.$route.params.workspaceID);
    },
    generateCode() {
      // canvasToZip();
    },
    externalMessageMouseup(message: Topic | Service) {
      if (!this.connectingNode) return;
      if (message instanceof Topic) {
        this.ncStore.connectingTopicNode = {
          node: this.connectingNode.id,
          topic: message.id
        };
      } else if (message instanceof Service) {
        this.ncStore.connectingServiceNode = {
          node: this.connectingNode,
          service: message
        };
      }
    },
    spawnCanvasObject(type: string, x: number, y: number) {
      switch (type) {
        case 'node':
          addNewNode(this.stage, x, y);
          break;
        case 'topic':
          addNewTopic(this.stage, x, y);
          break;
        case 'service':
          addNewService(this.stage, x, y);
          break;
      }
    },
    /**
     * Spawns canvas object at random location within canvas
     * @param type
     */
    spawnCanvasObjectRandomLocation(type: string) {
      let x = Math.floor(
        Math.random() *
          (this.$refs['node-canvas'] as HTMLCanvasElement).clientWidth
      );
      let y = Math.floor(
        Math.random() *
          (this.$refs['node-canvas'] as HTMLCanvasElement).clientHeight
      );
      this.spawnCanvasObject(type, x, y);
    }
  },
  computed: {
    console: () => console
  }
});

function fitToContainer(canvas: HTMLCanvasElement | null) {
  if (canvas === null) {
    return;
  }
  // Make it visually fill the positioned parent
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  // ...then set the internal size to match
  canvas.width = canvas.offsetWidth;
  canvas.height = canvas.offsetHeight;
}

interface NodeCanvasData {
  ncStore: Store<'nodeCanvas', NodeCanvasState, any>;
  globalStore: GlobalStoreState;
  editing: boolean;
  mouseInCanvas: boolean;
  editingNode: boolean;
  editingTopic: boolean;
  isShowParams: boolean;
  stage: any;
  db: any;
  connectingNode: null | Node;
  saveText: string;
}
